import { create } from 'zustand';
import { analytics } from '@mindstudio-ai/interface';
import api from '../api';

export type GuideState = NonNullable<Awaited<ReturnType<typeof api.getFirstRunGuide>>['guide']>;
export type GuideStep = 'idea' | 'strategist' | 'memo';
type Action = 'welcomeSeen' | 'memoOpened' | 'celebrated' | 'hide' | 'replay';

interface GuideStore {
  guide: GuideState | null;
  /** The first load has finished (or failed), so pages can decide what to show without a flash. */
  loaded: boolean;
  /** Welcome pop-up is open. */
  welcomeOpen: boolean;
  /** The spotlight is pointing at this step. */
  spotlight: GuideStep | null;
  /** The "How to start" card is open. */
  cardOpen: boolean;
  /** The card opened by itself after a step completed (collapses after a few seconds). */
  cardAuto: boolean;
  /** The first-memo announcement is showing. */
  memoCardOpen: boolean;
  load: () => Promise<void>;
  act: (action: Action, opts?: { brief?: boolean }) => Promise<void>;
  set: (patch: Partial<Pick<GuideStore, 'welcomeOpen' | 'spotlight' | 'cardOpen' | 'cardAuto' | 'memoCardOpen'>>) => void;
}

let inflight: Promise<void> | null = null;

export const useGuide = create<GuideStore>((set, get) => ({
  guide: null,
  loaded: false,
  welcomeOpen: false,
  spotlight: null,
  cardOpen: false,
  cardAuto: false,
  memoCardOpen: false,
  load: () => {
    if (inflight) return inflight;
    // Once it's off for this member, stop asking (Replay turns it back on through `act`).
    const cur = get().guide;
    if (cur && !cur.active) return Promise.resolve();
    inflight = api
      .getFirstRunGuide()
      .then(({ guide }) => {
        const prev = get().guide;
        set({ guide });
        // A step just got done (not on first load): open the card once so the tick lands where they can see it.
        if (prev && guide && prev.active && guide.active && prev.next !== guide.next) {
          if (prev.next) analytics.track('guide_step_completed', { step: prev.next });
          if (guide.next && guide.next !== 'memo' && !get().welcomeOpen && !get().spotlight) set({ cardOpen: true, cardAuto: true });
        }
      })
      .catch((err) => console.warn('[guide] load failed', err))
      .finally(() => {
        inflight = null;
        if (!get().loaded) set({ loaded: true });
      });
    return inflight;
  },
  act: async (action, opts) => {
    // Optimistic: the parts the member just dismissed go away at once.
    const g = get().guide;
    if (g) {
      if (action === 'welcomeSeen') set({ guide: { ...g, welcomeSeen: true, active: g.mode === 'brief' ? false : g.active } });
      if (action === 'hide' || action === 'celebrated') set({ guide: { ...g, active: false }, cardOpen: false, spotlight: null });
      if (action === 'memoOpened' && g.steps.memo === 'ready') set({ guide: { ...g, steps: { ...g.steps, memo: 'opened' }, next: null, finished: true } });
    }
    try {
      const { guide } = await api.updateFirstRunGuide({ action, ...(opts?.brief ? { brief: true } : {}) });
      set({ guide });
    } catch (err) {
      console.warn('[guide] update failed', err);
    }
  },
  set: (patch) => set(patch),
}));

/** Brings the welcome back ("Replay the welcome", avatar menu "How to start"). */
export async function replayGuide() {
  analytics.track('guide_replay');
  await useGuide.getState().act('replay');
  useGuide.getState().set({ welcomeOpen: true, cardOpen: false, spotlight: null });
}
